import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Fill-the-Blank drill: matrix displayed with some cells blanked out.
 * User fills in the missing values.
 */
export default function FillTheBlank({ matrix }) {
  const navigate = useNavigate();

  // Select which cells to blank (prioritize exam_hot)
  const blankedCells = useMemo(() => {
    if (!matrix) return [];
    const cells = [];
    for (let i = 0; i < matrix.attributes.length; i++) {
      const attr = matrix.attributes[i];
      for (const protocol of matrix.protocols) {
        if (attr.values[protocol] && attr.values[protocol] !== '—') {
          cells.push({
            attrIdx: i,
            protocol,
            correctValue: attr.values[protocol],
            attrName: attr.name,
            exam_hot: attr.exam_hot,
          });
        }
      }
    }
    // Prioritize exam_hot, shuffle, take ~30% of cells
    cells.sort((a, b) => (b.exam_hot ? 1 : 0) - (a.exam_hot ? 1 : 0));
    const count = Math.min(Math.max(5, Math.floor(cells.length * 0.3)), 15);
    return shuffleArray(cells).slice(0, count);
  }, [matrix]);

  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const blankedKeys = new Set(blankedCells.map(c => `${c.attrIdx}-${c.protocol}`));

  const handleChange = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => setSubmitted(true);

  const handleRetry = () => {
    setAnswers({});
    setSubmitted(false);
  };

  // Score
  const score = submitted
    ? blankedCells.reduce((acc, cell) => {
        const key = `${cell.attrIdx}-${cell.protocol}`;
        const userVal = (answers[key] || '').trim().toLowerCase();
        const correct = cell.correctValue.toLowerCase();
        return acc + (userVal === correct || correct.includes(userVal) ? 1 : 0);
      }, 0)
    : 0;

  if (!matrix) return null;

  return (
    <div className="px-4 py-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <button onClick={() => navigate(`/matrix/${matrix.id}`)} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
            ← Back to Matrix
          </button>
          <h2 className="text-xl font-bold text-gray-900">Fill the Blank: {matrix.title}</h2>
          <p className="text-sm text-gray-500">Fill in the {blankedCells.length} blanked cells from memory</p>
        </div>
        {!submitted ? (
          <button onClick={handleSubmit} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
            Check Answers
          </button>
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-lg font-bold" style={{
              color: score / blankedCells.length >= 0.8 ? '#2ECC71' : score / blankedCells.length >= 0.5 ? '#F39C12' : '#E74C3C'
            }}>
              {score}/{blankedCells.length} correct
            </span>
            <button onClick={handleRetry} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
              Try Again
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left font-medium text-gray-600 border-b border-r border-gray-200 min-w-[180px]">
                Attribute
              </th>
              {matrix.protocols.map(protocol => (
                <th key={protocol} className="px-4 py-3 text-left font-medium text-gray-900 border-b border-gray-200 min-w-[150px]">
                  {protocol}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.attributes.map((attr, attrIdx) => (
              <tr key={attrIdx} className={`border-b border-gray-100 ${
                attr.exam_hot ? 'bg-yellow-50' : attrIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
              }`}>
                <td className={`sticky left-0 z-10 px-4 py-3 font-medium border-r border-gray-200 ${
                  attr.exam_hot ? 'bg-yellow-50' : attrIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                }`}>
                  {attr.exam_hot && <span className="text-yellow-500 text-xs mr-1">★</span>}
                  {attr.name}
                </td>
                {matrix.protocols.map(protocol => {
                  const key = `${attrIdx}-${protocol}`;
                  const isBlanked = blankedKeys.has(key);
                  const value = attr.values[protocol] || '—';
                  const cell = blankedCells.find(c => c.attrIdx === attrIdx && c.protocol === protocol);

                  if (!isBlanked) {
                    return <td key={protocol} className="px-4 py-3 text-gray-700">{value}</td>;
                  }

                  if (submitted) {
                    const userVal = (answers[key] || '').trim().toLowerCase();
                    const correct = cell.correctValue.toLowerCase();
                    const isCorrect = userVal === correct || correct.includes(userVal);

                    return (
                      <td key={protocol} className={`px-4 py-3 ${isCorrect ? 'bg-green-50' : 'bg-red-50'}`}>
                        <div className={`text-sm font-medium ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                          {answers[key] || '(empty)'}
                        </div>
                        {!isCorrect && (
                          <div className="text-xs text-gray-500 mt-1">
                            Correct: {cell.correctValue}
                          </div>
                        )}
                      </td>
                    );
                  }

                  return (
                    <td key={protocol} className="px-2 py-2">
                      <input
                        type="text"
                        value={answers[key] || ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                        placeholder="?"
                        className="w-full px-2 py-1 border border-blue-300 rounded bg-blue-50 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
